(function () {
  'use strict';

  class FrequentlyBoughtTogether extends HTMLElement {
    connectedCallback() {
      this._totalEl = this.querySelector('.fbt-total__price');
      this._originalEl = this.querySelector('.fbt-total__original');
      this._savingsBadge = this.querySelector('.fbt-savings-badge');
      this._atcBtn = this.querySelector('.fbt-atc');
      this._errorEl = this.querySelector('.fbt-error');
      this._discount = parseInt(this.dataset.discount || '0', 10);

      // Pre-populate sunshades FBT item from SunshadesData if present
      const snshItem = this.querySelector('[data-snsh-fbt-item]');
      if (snshItem && window.SunshadesData) {
        const pieces = Array.isArray(window.SunshadesData.pieces) ? window.SunshadesData.pieces : [];
        const bundles = Array.isArray(window.SunshadesData.bundles) ? window.SunshadesData.bundles : [];
        const variantIds = pieces.filter(p => p.variant_id).map(p => p.variant_id);
        const fullTotal = pieces.reduce((sum, p) => sum + (p.price || 0), 0);

        // Check if all available pieces match a bundle
        const pieceIds = new Set(pieces.filter(p => p.variant_id).map(p => p.id));
        const matchedBundle = bundles.find(b =>
          b.pieces.length === pieceIds.size && b.pieces.every(pid => pieceIds.has(pid))
        );
        let total = fullTotal;
        if (matchedBundle && matchedBundle.discount) {
          const disc = matchedBundle.discount > 1 ? matchedBundle.discount / 100 : matchedBundle.discount;
          total = fullTotal * (1 - disc);
        }

        const totalCents = Math.round(total * 100);
        snshItem.dataset.price = totalCents;
        snshItem.dataset.variantIds = variantIds.join(',');
        const priceEl = snshItem.querySelector('.fbt-item__price');
        if (priceEl) priceEl.textContent = this._formatMoney(totalCents);
        const countEl = snshItem.querySelector('.fbt-snsh-piece-count');
        if (countEl) countEl.textContent = `${variantIds.length} piece${variantIds.length !== 1 ? 's' : ''} selected`;
      }

      this._updateTotal();

      // Sync first item when main product variant changes
      this._onVariantChange = (e) => {
        const variant = e.detail?.variant;
        if (!variant) return;
        const firstItem = this.querySelectorAll('.fbt-item')[0];
        if (!firstItem) return;
        firstItem.dataset.variantId = variant.id;
        firstItem.dataset.price = variant.price;
        const priceEl = firstItem.querySelector('.fbt-item__price');
        if (priceEl) priceEl.textContent = this._formatMoney(variant.price);
        this._updateTotal();
      };
      document.addEventListener('on:variant:change', this._onVariantChange);

      // Sync with sunshades piece selector
      this._onSunshadesSelection = (e) => {
        const snshItem = this.querySelector('[data-snsh-fbt-item]');
        if (!snshItem) return;
        const { variantIds, total } = e.detail;
        const totalCents = Math.round(total * 100);
        snshItem.dataset.variantIds = variantIds.join(',');
        snshItem.dataset.price = totalCents;
        const priceEl = snshItem.querySelector('.fbt-item__price');
        if (priceEl) priceEl.textContent = variantIds.length ? this._formatMoney(totalCents) : '—';
        const countEl = snshItem.querySelector('.fbt-snsh-piece-count');
        if (countEl) countEl.textContent = variantIds.length ? `${variantIds.length} piece${variantIds.length > 1 ? 's' : ''} selected` : 'None selected';
        const cb = snshItem.querySelector('.fbt-item__check');
        const hasItems = variantIds.length > 0;
        if (cb) cb.checked = hasItems;
        snshItem.classList.toggle('is-unchecked', !hasItems);
        this._updateTotal();
      };
      document.addEventListener('on:sunshades:selection', this._onSunshadesSelection);

      this.addEventListener('change', (e) => {
        if (e.target.classList.contains('fbt-item__check')) {
          const item = e.target.closest('.fbt-item');
          if (item) item.classList.toggle('is-unchecked', !e.target.checked);
          this._updateTotal();
        }
        if (e.target.classList.contains('fbt-item__variant-select')) {
          const item = e.target.closest('.fbt-item');
          const opt = e.target.options[e.target.selectedIndex];
          if (item && opt) {
            item.dataset.variantId = opt.value;
            item.dataset.price = opt.dataset.price;
            const priceEl = item.querySelector('.fbt-item__price');
            if (priceEl) priceEl.textContent = this._formatMoney(parseInt(opt.dataset.price, 10));
          }
          this._updateTotal();
        }
      });

      if (this._atcBtn) {
        this._atcBtn.addEventListener('click', () => this._addToCart());
      }
    }

    disconnectedCallback() {
      if (this._onVariantChange) {
        document.removeEventListener('on:variant:change', this._onVariantChange);
      }
      if (this._onSunshadesSelection) {
        document.removeEventListener('on:sunshades:selection', this._onSunshadesSelection);
      }
    }

    _checkedItems() {
      return Array.from(this.querySelectorAll('.fbt-item')).filter(item => {
        const cb = item.querySelector('.fbt-item__check');
        return cb && cb.checked && (item.dataset.variantId || item.dataset.variantIds);
      });
    }

    _updateTotal() {
      const checked = this._checkedItems();
      const snshItem = this.querySelector('[data-snsh-fbt-item]');
      const snshCb = snshItem && snshItem.querySelector('.fbt-item__check');
      const sunshadeChecked = snshCb && snshCb.checked;

      let originalTotal = 0;
      let discountedTotal = 0;

      for (const item of checked) {
        const price = parseInt(item.dataset.price || 0, 10);
        originalTotal += price;

        const crossSellPct = parseInt(item.dataset.crossSellPct || 0, 10);
        if (crossSellPct > 0 && sunshadeChecked) {
          discountedTotal += Math.round(price * (1 - crossSellPct / 100));
        } else {
          discountedTotal += price;
        }
      }

      // Apply global FBT discount on top if configured
      const applyGlobalDiscount = this._discount > 0 && checked.length >= 2;
      if (applyGlobalDiscount) {
        discountedTotal = Math.round(discountedTotal * (1 - this._discount / 100));
      }

      const savings = originalTotal - discountedTotal;
      const hasAnySavings = savings > 0;

      // Update per-item cross-sell price display + pill badge
      this.querySelectorAll('.fbt-item[data-cross-sell-pct]').forEach(item => {
        const priceEl = item.querySelector('.fbt-item__price');
        if (!priceEl) return;
        const price = parseInt(item.dataset.price || 0, 10);
        const pct = parseInt(item.dataset.crossSellPct || 0, 10);
        let pill = item.querySelector('.fbt-cross-sell-pill');
        if (pct > 0 && sunshadeChecked) {
          const discounted = Math.round(price * (1 - pct / 100));
          priceEl.innerHTML = `<span style="display:flex;flex-direction:column;align-items:flex-end;gap:1px"><span>${this._formatMoney(discounted)}</span><s style="opacity:.45;font-weight:400;font-size:.85em">${this._formatMoney(price)}</s></span>`;
          if (!pill) {
            pill = document.createElement('span');
            pill.className = 'fbt-cross-sell-pill';
            const infoEl = item.querySelector('.fbt-item__info');
            if (infoEl) infoEl.appendChild(pill);
          }
          pill.textContent = `${pct}% off`;
          pill.style.display = '';
        } else {
          priceEl.textContent = this._formatMoney(price);
          if (pill) pill.style.display = 'none';
        }
      });

      // Original price (strikethrough) — only show when there are savings
      if (this._originalEl) {
        if (hasAnySavings) {
          this._originalEl.textContent = this._formatMoney(originalTotal);
          this._originalEl.style.display = '';
        } else {
          this._originalEl.textContent = '';
          this._originalEl.style.display = 'none';
        }
      }

      // Final price
      if (this._totalEl) {
        this._totalEl.textContent = this._formatMoney(discountedTotal);
      }

      // Savings badge
      if (this._savingsBadge) {
        if (hasAnySavings) {
          this._savingsBadge.textContent = `You save ${this._formatMoney(savings)}`;
          this._savingsBadge.style.display = '';
        } else if (this._discount > 0 && checked.length === 1) {
          this._savingsBadge.textContent = `Add 1 more to save ${this._discount}%`;
          this._savingsBadge.style.display = '';
        } else {
          this._savingsBadge.textContent = '';
          this._savingsBadge.style.display = 'none';
        }
      }

      if (this._atcBtn) {
        this._atcBtn.disabled = checked.length === 0;
        this._atcBtn.textContent = checked.length > 1
          ? `Add ${checked.length} items — ${this._formatMoney(discountedTotal)}`
          : checked.length === 1
            ? `Add to Cart — ${this._formatMoney(discountedTotal)}`
            : 'Select at least 1 item';
      }
    }

    async _addToCart() {
      if (this._errorEl) this._errorEl.textContent = '';

      const items = [];
      this._checkedItems().forEach(item => {
        if (item.dataset.variantIds) {
          item.dataset.variantIds.split(',').filter(Boolean).forEach(id => {
            items.push({ id: parseInt(id, 10), quantity: 1 });
          });
        } else if (item.dataset.variantId) {
          items.push({ id: parseInt(item.dataset.variantId, 10), quantity: 1 });
        }
      });

      if (!items.length) return;

      this._atcBtn.classList.add('is-loading');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          let msg = 'Could not add to cart. Please try again.';
          try {
            const data = await res.json();
            if (data.description) msg = data.description;
            else if (data.message) msg = data.message;
          } catch (_) {}
          throw new Error(msg);
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        window.location.href = '/cart';
      } catch (err) {
        if (this._errorEl) this._errorEl.textContent = err.message;
      } finally {
        this._atcBtn.classList.remove('is-loading');
      }
    }

    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  if (!customElements.get('fbt-selector')) {
    customElements.define('fbt-selector', FrequentlyBoughtTogether);
  }
})();